import { Button } from '@mui/material';
import { Badge, Tabs } from 'antd';
import { NotificationItem } from 'features/notification/components/NotificationItem';
import { NOTIFICATION_PANEL_PAGE_SIZE } from 'features/notification/constants';
import { formatUnreadBadge } from 'features/notification/helpers';
import {
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
} from 'features/notification/query';
import { NOTIFICATION_CATEGORY } from 'features/notification/types';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { ArrowRightOutlineIcon } from 'shared/components/AppIcon';
import { EmptyContent } from 'features/notification/components/NotificationPanel/constants';
import { type TPanelTab } from 'features/notification/components/NotificationPanel/types';

type NotificationContentProps = {
  onClose: () => void;
};

export const NotificationContent = memo(
  ({ onClose }: NotificationContentProps) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TPanelTab>(
      NOTIFICATION_CATEGORY.PERSONAL
    );

    const { data: unreadCount } = useGetNotificationUnreadCountQuery(
      undefined,
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
      }
    );

    const { data: personalData } = useGetNotificationsQuery(
      {
        category: NOTIFICATION_CATEGORY.PERSONAL,
        page: 1,
        size: NOTIFICATION_PANEL_PAGE_SIZE,
      },
      {
        skip: activeTab !== NOTIFICATION_CATEGORY.PERSONAL,
        refetchOnMountOrArgChange: true,
      }
    );

    const { data: companyData } = useGetNotificationsQuery(
      {
        category: NOTIFICATION_CATEGORY.COMPANY,
        page: 1,
        size: NOTIFICATION_PANEL_PAGE_SIZE,
      },
      {
        skip: activeTab !== NOTIFICATION_CATEGORY.COMPANY,
        refetchOnMountOrArgChange: true,
      }
    );

    const [markRead] = useMarkNotificationReadMutation();

    const handleReadItem = useCallback(
      (id: string) => {
        void markRead(id);
      },
      [markRead]
    );

    const goToNotificationList = useCallback(() => {
      onClose();
      navigate(
        getPath(
          activeTab === NOTIFICATION_CATEGORY.COMPANY
            ? 'newsListPage'
            : 'notificationListPage'
        )
      );
    }, [activeTab, navigate, onClose]);

    const renderList = useCallback(
      (category: TPanelTab) => {
        const items =
          (category === NOTIFICATION_CATEGORY.PERSONAL
            ? personalData
            : companyData
          )?.data ?? [];

        if (items.length === 0) return EmptyContent;

        return (
          <div className="space-y-6 overflow-y-auto min-769:max-h-[60vh]">
            {items.map(item => (
              <NotificationItem
                key={item.id}
                data={item}
                viewMode="panel"
                showTag={category !== NOTIFICATION_CATEGORY.COMPANY}
                onRead={handleReadItem}
                onNavigate={() => onClose()}
              />
            ))}
          </div>
        );
      },
      [companyData, handleReadItem, onClose, personalData]
    );

    const renderTabLabel = useCallback(
      (label: string, count?: number) => (
        <span className="flex items-center gap-1.5 text-xs">
          {label}
          {Boolean(count) && (
            <Badge
              count={formatUnreadBadge(count)}
              color="#4680FF"
              className="[&_.ant-badge-count]:text-[10px]"
            />
          )}
        </span>
      ),
      []
    );

    const tabItems = useMemo(
      () => [
        {
          key: NOTIFICATION_CATEGORY.PERSONAL,
          label: renderTabLabel('Thông báo', unreadCount?.personal),
          children: renderList(NOTIFICATION_CATEGORY.PERSONAL),
        },
        {
          key: NOTIFICATION_CATEGORY.COMPANY,
          label: renderTabLabel('Thông báo chung', unreadCount?.general),
          children: renderList(NOTIFICATION_CATEGORY.COMPANY),
        },
      ],
      [renderList, renderTabLabel, unreadCount?.general, unreadCount?.personal]
    );

    return (
      <>
        <Tabs
          className="[&_.ant-tabs-nav]:mb-5 [&_.ant-tabs-tab]:pt-0"
          activeKey={activeTab}
          onChange={key => setActiveTab(key as TPanelTab)}
          items={tabItems}
        />

        <div className="mt-4 flex items-center justify-center">
          <Button
            variant="text"
            className="gap-3 text-sm font-medium normal-case text-primary"
            endIcon={<ArrowRightOutlineIcon className="h-4 w-4 -rotate-45" />}
            onClick={goToNotificationList}
          >
            Xem thêm
          </Button>
        </div>
      </>
    );
  }
);
